import { Dispatch, SetStateAction, useMemo, useState } from 'react'
import { InputTableLayoutData, InputTableURLsTable, OrderHeaderURLs } from './types'
import { UserPermissions } from '../../../baseComponents/CreateRoutering/types'
import { BigTitleAndRightLayout } from '../../../baseComponents/Layout/TitleAndRightLayout'
import { WhiteBox } from '../../../baseComponents/AppLayout/WhiteBox'
import { TableData, TableConfiguration, HeaderChangesTy } from '../../../baseComponents/TwinTable/types'
import LoaderDecider from '../../../baseComponents/LoaderDecider'
import TwinIcon from '../../../baseComponents/TwinIcon'
import { faBars, faChevronDown, faChevronUp, faTimes } from '@fortawesome/pro-light-svg-icons'
import { ColumnITLayoutTableConfiguration, InputTableFooterFullProps, ITHookFunctionTy, } from './InputTable/types'
import InputTable, { InputTableNew, SearchBarInputTable } from './InputTable'
import { DictionaryComponents, Modify } from '../../../utils/globals/types'
import { TableWithoutVirtualListingRowProps, TableWithoutVirtualListingRowRenderer } from '../TableWithoutVirtualListing/types'
import './inputtablelayout.sass'
import { TwinDictionary } from '../../../utils/globals/dictionary'
import { OrderLayoutTableFilter } from '../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderTable/useOrderLayoutTableLogic/types'
import { ConnectedComponent } from 'react-redux'
import { ButtonConfigColumnsWithModal } from '../../ModalsLayouts/ModalConfigColumnsTabs'
import { useCustomerParsedLinkMemo } from '../../../utils/hooks/customer/useCustomerTypesDict'
import { Link } from 'react-router-dom'
import useConfigColumns from '../../../utils/hooks/useConfigColumns'
import locations from '../../../utils/globals/locations'
import { CodeCustomerToShowTyInversed } from '@teinor/erp/types/company/config/parameter'
import { getConfigParam } from '../../../utils/reducers/getters'

export interface InputTableLayoutProps extends InputTableLayoutLogicProps {
    name: string
    translations: {
        headerTitle: string
    }
    children?: React.ReactNode
    buttonSave?: JSX.Element
    inputTableURLs: InputTableURLsTable
    orderHeaderURLs: OrderHeaderURLs
    getCompleteData: (vat?: TableData) => Promise<void>
    tableConfiguration: TableConfiguration
    setTableConfiguration: React.Dispatch<React.SetStateAction<TableConfiguration>>
    rowRenderer?: TableWithoutVirtualListingRowRenderer
    completeData: InputTableLayoutData | null
    setCompleteData: Dispatch<SetStateAction<InputTableLayoutData | null>>
    userPermissions: UserPermissions
    columns: ColumnITLayoutTableConfiguration[]
    hideNotes?: boolean
    hideLeftFooter?: boolean
    hideFinancialRows?: boolean
    components?: DictionaryComponents
    className?: string
    headerChanges?: HeaderChangesTy
    filters?: OrderLayoutTableFilter
    orderStatusDict: TwinDictionary
    InputTableHeaderChildren: ConnectedComponent<React.FC<any>, any>
    ITHeaderChildrenExtraData?: TwinDictionary
    isReadOnly?: boolean
    modelId: string
    myAddExtraProps?: (props: TableWithoutVirtualListingRowProps) => { className: string }
}
interface InputTableLayoutFullProps extends InputTableLayoutProps {
    hookFunction: ITHookFunctionTy
    TableChildren: React.FC<InputTableFooterFullProps>
    BigAndTitleRightLayout: React.FC<any>
}

const InputTableLayout: React.FC<InputTableLayoutFullProps> = ({ name, translations, children, buttonSave, columns, completeData, className, InputTableHeaderChildren, ITHeaderChildrenExtraData, ...rest }) => {
    const { active, setActive } = useInputTableLayoutLogic()
    return (
        <LoaderDecider loading={completeData === null}>
            <BigTitleAndRightLayout title={translations.headerTitle} className='overflow-y-auto input_table_layout' RightHeader={buttonSave}>
                <WhiteBox className={'input_table_whitebox ' + (active ? 'active' : '')}>
                    <div className='input_table_showicon' onClick={() => setActive((old) => !old)}><TwinIcon icon={active ? faTimes : faBars} /></div>
                    <div className={'input_table_header_column'}>
                        <div className='input_table_header_col_animating flex h-full'>
                            <InputTableHeaderChildren headerData={completeData?.header} updateDataURL={rest?.inputTableURLs?.updateHeaderData || ''} tableData={completeData?.tableData} setCompleteData={rest?.setCompleteData} getCompleteData={rest?.getCompleteData} userPermissions={rest?.userPermissions} {...ITHeaderChildrenExtraData} />
                            {children}
                        </div>
                    </div>
                    <InputTable CurrencyId={completeData?.header?.CurrencyId || ''} completeData={completeData || { header: {}, tableData: [], originalTableData: [], dictPosTableData: {}, filters: { generalSearchbar: '', order: {}, singleFieldsSearch: {} } }} {...{ name, columns, className, ...rest }} />
                </WhiteBox>
            </BigTitleAndRightLayout>
        </LoaderDecider>
    )
}

interface InputTableLayoutLogicProps { }

const useInputTableLayoutLogic = () => {
    const [active, setActive] = useState(true)
    return { active, setActive }
}

export const InputTableLayoutNew: React.FC<InputTableLayoutFullProps> = ({ completeData, ...rest }) => {
    return (
        <LoaderDecider loading={completeData === null}>
            <InputTableLayoutNewLoaded completeData={completeData as InputTableLayoutData} {...rest}/>
        </LoaderDecider>
    )
}

type InputTableLayoutNewLoadedProps = Modify<InputTableLayoutFullProps, {
    completeData: InputTableLayoutData
}>

const InputTableLayoutNewLoaded: React.FC<InputTableLayoutNewLoadedProps> = ({ name, translations, buttonSave, columns, completeData, className, InputTableHeaderChildren, children, ITHeaderChildrenExtraData, BigAndTitleRightLayout, hookFunction, ...rest }) => {
    const { setSelectedColumnsAndFilters, selectedColumnsAndFilters, memorizedColumns } = useConfigColumns({ columns, name })
    const { onChangeSearchBar, ...resty } = hookFunction({ completeData, columns: memorizedColumns, CurrencyId: completeData?.header?.CurrencyId, ...rest })
    const { active, setActive } = useInputTableLayoutNewLogic()
    const fullHeaderTitle = translations.headerTitle + ' ' + (completeData?.header?.full_assigned_number|| '')
    return (
        <BigAndTitleRightLayout title={fullHeaderTitle} className='overflow-y-auto input_table_layout' RightHeader={buttonSave} Customer={completeData?.header?.Customer} Supplier={completeData?.header?.Supplier}>
            <WhiteBox className='input_table_whitebox input_table_new_whitebox'>
                <div className={'input_table_top_header_parent flex relative ' + (active ? 'active' : '')}>
                    <div className='flex input_table_new_showicon cursor-pointer absolute bg-white rounded-full items-center justify-center z-3 regular16' onClick={() => setActive((old) => !old)}><TwinIcon icon={active ? faChevronUp : faChevronDown} /></div>
                    <div className='flex gap-x-15 input_table_top_header_content p-20 flex-auto'>
                        <SearchBarInputTable onChange={onChangeSearchBar} />
                        <InputTableHeaderChildren headerData={completeData?.header} updateDataURL={rest?.inputTableURLs?.updateHeaderData} tableData={completeData?.tableData} setCompleteData={rest?.setCompleteData} getCompleteData={rest?.getCompleteData} userPermissions={rest?.userPermissions} {...ITHeaderChildrenExtraData} />
                        <ButtonConfigColumnsWithModal name={name} columns={columns} setSelectedColumnsAndFilters={setSelectedColumnsAndFilters} selectedColumnsAndFilters={selectedColumnsAndFilters} haveFiltersColumn={false} setTableConfiguration={() => { }} getTableDataFromStart={() => { }} />
                    </div>
                    {children}
                </div>
                <InputTableNew CurrencyId={completeData?.header?.CurrencyId || ''} completeData={completeData || { header: {}, tableData: [], originalTableData: [], dictPosTableData: {}, filters: { generalSearchbar: '', order: {}, singleFieldsSearch: {} } }} {...{ name, columns, className, setSelectedColumnsAndFilters, selectedColumnsAndFilters, memorizedColumns, ...rest }} hookFunction={hookFunction} {...resty} onChangeSearchBar={onChangeSearchBar} />
            </WhiteBox>
        </BigAndTitleRightLayout>
    )
}

const useInputTableLayoutNewLogic = () => {
    const [active, setActive] = useState(true)
    return { active, setActive }
}

interface BigTitleAndRightLayoutInputTableNewCustomerProps {
    title: string
    RightHeader?: JSX.Element
    children: React.ReactNode
    className?: string
    Customer: TwinDictionary
}

export const BigTitleAndRightLayoutInputTableNewCustomer: React.FC<BigTitleAndRightLayoutInputTableNewCustomerProps> = ({ title, children, RightHeader, Customer, className }) => {
    const { parsedLinkMemo } = useCustomerParsedLinkMemo({ id: Customer?.id, customer_type: Customer?.customer_type })
    const fieldMemo = useMemo(() => {
        const codeCustomerToShow = getConfigParam('codeCustomerToShow')
        let fieldId: 'id' | 'code' = 'id'
        if (parseInt(String(codeCustomerToShow)) === parseInt(String(CodeCustomerToShowTyInversed['codeCustomer']))) {
            fieldId = 'code'
        }
        return fieldId
    }, [])
    const idOrCode = Customer?.[fieldMemo] ? (Customer[fieldMemo] + ' ') : ''
    const defaultTitle = idOrCode + (Customer?.fiscal_name || Customer?.name)
    return (
        <div className={'px-40 py-45 flex flex-col flex-auto big-layout input_table_new_title ' + (className || '')}>
            <div className='flex mb-30 gap-y-10 gap-x-20 flex-wrap big_layout_title'>
                <div className='flex items-center flex-wrap gap-x-10 input_table_main_title'>
                    <h1>{title}</h1>
                    {Customer?.id ?
                        <div className='flex flex-row items-center gap-x-10 regular26'>
                            <span>-</span>
                            <Link className='cursor-pointer hover:text-green-21' to={parsedLinkMemo}>
                                {defaultTitle}
                            </Link>
                        </div>
                    : null}
                </div>
                {RightHeader}
            </div>
            {children}
        </div>
    )
}

interface BigTitleAndRightLayoutInputTableNewProps {
    title: string
    RightHeader?: JSX.Element
    children: React.ReactNode
    className?: string
    Supplier: TwinDictionary
}

export const BigTitleAndRightLayoutInputTableSupplier: React.FC<BigTitleAndRightLayoutInputTableNewProps> = ({ title, children, RightHeader, className, Supplier }) => {
    const supplierCode = Supplier?.code ? (Supplier?.code + ' ') : ''
    const defaultTitle = supplierCode + (Supplier?.fiscal_name || Supplier?.name)
    const defaultTitleLink = locations.suppliers + '/' + Supplier.id + '/main'
    return (
        <div className={'px-40 py-45 flex flex-col flex-auto big-layout input_table_new_title ' + (className || '')}>
            <div className='flex mb-30 gap-y-10 gap-x-20 flex-wrap big_layout_title'>
                <div className='flex items-center flex-wrap gap-x-10 input_table_main_title'>
                    <h1>{title} - </h1>
                    <div className='flex flex-row items-center gap-x-10 regular26'>
                        <Link className='cursor-pointer hover:text-green-21' to={defaultTitleLink}>
                            {defaultTitle}
                        </Link>
                    </div>
                </div>
                {RightHeader}
            </div>
            {children}
        </div>
    )
}

export default InputTableLayout